import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Opportunity,
  OpportunityDocument,
  ProposalResponse,
  ResponseSection,
  ResponseTemplate,
  LaborPosition,
  MaterialItem,
  OverheadItem,
  RevenueItem,
  RfpAnalysis,
  Assumptions,
  OtherInsuranceItem,
} from "../types";
import {
  getOpportunity,
  getResponses,
  saveResponse,
  updateResponse,
  getTemplates,
  getDocuments,
  uploadDocument,
  analyzeRfp,
  getRfpAnalysis,
} from "../api";
import { useAuth } from "../context/AuthContext";

// ── Helpers ──────────────────────────────────────────────────────────────────

const newId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const fmtPct = (n: number) =>
  `${n >= 0 ? "" : ""}${n.toFixed(1)}%`;

const DEFAULT_ASSUMPTIONS: Assumptions = {
  contract_years: 1,
  workers_comp_rate: 0,
  general_liability_pct: 0,
  unemployment_pct: 0,
  unemployment_wage_base: 0,
  unemployment_headcount_pct: 100,
  passthrough_rate_pct: 0,
  city_tax_rate_pct: 0,
  employee_benefits_cost: 0,
  benefits_participation_pct: 0,
  yearly_revenue_increase_pct: 0,
  yearly_wage_increase_pct: 0,
  yearly_materials_increase_pct: 0,
  yearly_overhead_increase_pct: 0,
  other_insurance: [],
};

// ── Tax & Insurance calc from assumptions + labor ────────────────────────────

const FICA_RATE = 0.062;     // 6.2% employer FICA
const MEDICARE_RATE = 0.0145; // 1.45% employer Medicare

function calcTaxInsurance(labor: LaborPosition[], assumptions: Assumptions) {
  const totalPayroll = labor.reduce(
    (s, p) => s + (p.headcount || 0) * (p.weekly_hours || 0) * 52 * (p.wage || 0), 0
  );
  const totalHeadcount = labor.reduce((s, p) => s + (p.headcount || 0), 0);

  const fica = totalPayroll * FICA_RATE;
  const medicare = totalPayroll * MEDICARE_RATE;
  // Unemployment = wage_base × rate × total_headcount × unemployment_headcount_%
  const unemploymentHeadcount = totalHeadcount * ((assumptions.unemployment_headcount_pct || 0) / 100);
  const unemployment = (assumptions.unemployment_wage_base || 0) * ((assumptions.unemployment_pct || 0) / 100) * unemploymentHeadcount;
  // Workers' Comp = total payroll × rate
  const workersComp = totalPayroll * ((assumptions.workers_comp_rate || 0) / 100);
  // General Liability = total payroll × rate
  const generalLiability = totalPayroll * ((assumptions.general_liability_pct || 0) / 100);
  const otherTotal = (assumptions.other_insurance || []).reduce((s, o) => s + (o.annual_cost || 0), 0);
  // Employee Benefits = total_headcount × cost × participation_%
  const benefits = totalHeadcount * (assumptions.employee_benefits_cost || 0) * ((assumptions.benefits_participation_pct || 0) / 100);

  const taxInsTotal = fica + medicare + unemployment + workersComp + generalLiability + otherTotal;

  return { totalPayroll, totalHeadcount, fica, medicare, unemployment, unemploymentHeadcount,
    workersComp, generalLiability, otherTotal, benefits, taxInsTotal,
    total: taxInsTotal + benefits };
}

// ── Budget calculation ────────────────────────────────────────────────────────

function calcBudget(
  labor: LaborPosition[],
  materials: MaterialItem[],
  overhead: OverheadItem[],
  revenue: RevenueItem[],
  taxInsTotal: number,
  benefitsTotal: number
) {
  const totalRevenue = revenue.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalLabor = labor.reduce(
    (s, p) => s + (p.headcount || 0) * (p.weekly_hours || 0) * 52 * (p.wage || 0),
    0
  );
  const totalMaterials = materials.reduce(
    (s, m) => s + (m.quantity || 0) * (m.unit_cost || 0),
    0
  );
  const totalOverhead = overhead.reduce((s, o) => s + (o.amount || 0), 0);
  const grossMargin = totalRevenue - totalLabor - taxInsTotal - benefitsTotal - totalMaterials - totalOverhead;
  const grossMarginPct = totalRevenue > 0 ? (grossMargin / totalRevenue) * 100 : 0;
  return { totalRevenue, totalLabor, totalMaterials, totalOverhead, grossMargin, grossMarginPct };
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: "7px 10px",
  border: "1px solid #ddd",
  borderRadius: "4px",
  fontSize: "13px",
  fontFamily: "Segoe UI, sans-serif",
  boxSizing: "border-box",
};

const btnPrimary: React.CSSProperties = {
  padding: "7px 16px",
  backgroundColor: "#27ae60",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 600,
};

const btnSecondary: React.CSSProperties = {
  padding: "7px 16px",
  backgroundColor: "white",
  color: "#1a5276",
  border: "1px solid #1a5276",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 600,
};

const btnDanger: React.CSSProperties = {
  padding: "4px 10px",
  backgroundColor: "white",
  color: "#e74c3c",
  border: "1px solid #e74c3c",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "12px",
};

const card: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
  padding: "20px",
  marginBottom: "16px",
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#666",
  backgroundColor: "#f8f9fa",
  borderBottom: "1px solid #e0e0e0",
};

const td: React.CSSProperties = {
  padding: "6px 6px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
};

// ── Tab 1: Info & Files ──────────────────────────────────────────────────────

function InfoTab({
  opp,
  documents,
  docsLoading,
  budget,
  taxIns,
  oppId,
  onDocumentsRefresh,
}: {
  opp: Opportunity;
  documents: OpportunityDocument[];
  docsLoading: boolean;
  budget: ReturnType<typeof calcBudget>;
  taxIns: ReturnType<typeof calcTaxInsurance>;
  oppId: string;
  onDocumentsRefresh: () => void;
}) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadDocType, setUploadDocType] = React.useState("attachment");
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!pendingFile || !oppId) return;
    try {
      setUploading(true);
      await uploadDocument(oppId, pendingFile, uploadDocType);
      setPendingFile(null);
      setUploadDocType("attachment");
      onDocumentsRefresh();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const value = opp.estimated_value
    ? `$${Number(opp.estimated_value).toLocaleString()}`
    : "Not specified";
  const deadline = opp.response_deadline
    ? new Date(opp.response_deadline).toLocaleDateString("en-US")
    : "Not specified";

  const budgetRows = [
    { label: "Total Revenue", value: budget.totalRevenue, color: "#27ae60" },
    { label: "Labor (annualized)", value: budget.totalLabor, color: "#2980b9" },
    { label: "Tax & Insurance", value: taxIns.taxInsTotal, color: "#e67e22" },
    { label: "Employee Benefits", value: taxIns.benefits, color: "#9b59b6" },
    { label: "Materials", value: budget.totalMaterials, color: "#8e44ad" },
    { label: "Site Overhead", value: budget.totalOverhead, color: "#e67e22" },
    {
      label: "Gross Margin",
      value: budget.grossMargin,
      color: budget.grossMargin >= 0 ? "#27ae60" : "#e74c3c",
    },
  ];

  return (
    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
      {/* Left column */}
      <div style={{ flex: "1 1 420px" }}>
        {/* Opportunity details */}
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", fontSize: "15px", color: "#2c3e50" }}>
            Opportunity Details
          </h3>
          <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
            <tbody>
              {(
                [
                  ["Agency", opp.agency],
                  ["Office", opp.office],
                  ["Notice ID", opp.notice_id],
                  ["NAICS", opp.naics_code],
                  ["Set-Aside", opp.set_aside || "Unrestricted"],
                  ["Est. Value", value],
                  ["Location", opp.place_of_performance || opp.state || "N/A"],
                  ["Deadline", deadline],
                  ["Gate 2 Score", opp.total_score != null ? `${opp.total_score}/100` : "N/A"],
                  ["AI Result", opp.gate3_result || "N/A"],
                ] as [string, string][]
              ).map(([label, val]) => (
                <tr key={label}>
                  <td
                    style={{
                      padding: "6px 12px 6px 0",
                      color: "#999",
                      fontWeight: 500,
                      width: "130px",
                      verticalAlign: "top",
                    }}
                  >
                    {label}
                  </td>
                  <td style={{ padding: "6px 0", color: "#2c3e50" }}>{val || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {opp.url && (
            <a
              href={opp.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: "14px",
                padding: "8px 16px",
                backgroundColor: "#1a5276",
                color: "white",
                borderRadius: "4px",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              ↗ View Original RFP Posting
            </a>
          )}
        </div>

        {/* AI Reasoning */}
        {opp.gate3_reasoning && (
          <div style={card}>
            <h3 style={{ margin: "0 0 10px", fontSize: "14px", color: "#2c3e50" }}>
              AI Assessment
            </h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#444", lineHeight: 1.6 }}>
              {opp.gate3_reasoning}
            </p>
          </div>
        )}
      </div>

      {/* Right column */}
      <div style={{ flex: "1 1 340px" }}>
        {/* Budget Summary */}
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", fontSize: "15px", color: "#2c3e50" }}>
            Budget Summary
          </h3>
          <p style={{ margin: "0 0 12px", fontSize: "12px", color: "#888" }}>
            Auto-calculated from all schedules. Labor annualized at 52 weeks.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {budgetRows.map(({ label, value: v, color }) => (
                <tr key={label}>
                  <td
                    style={{
                      padding: "9px 0",
                      fontSize: "13px",
                      color: "#555",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    {label}
                  </td>
                  <td
                    style={{
                      padding: "9px 0",
                      fontSize: "14px",
                      fontWeight: 700,
                      color,
                      textAlign: "right",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    {fmt(v)}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: "9px 0", fontSize: "13px", color: "#555" }}>
                  Gross Margin %
                </td>
                <td
                  style={{
                    padding: "9px 0",
                    fontSize: "14px",
                    fontWeight: 700,
                    color: budget.grossMarginPct >= 0 ? "#27ae60" : "#e74c3c",
                    textAlign: "right",
                  }}
                >
                  {fmtPct(budget.grossMarginPct)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Documents */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ margin: 0, fontSize: "15px", color: "#2c3e50" }}>
              Documents
            </h3>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={btnPrimary}
              disabled={uploading}
            >
              + Add Document
            </button>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </div>

          {/* Pending file upload bar */}
          {pendingFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 12px",
                backgroundColor: "#fef9e7",
                borderRadius: "6px",
                border: "1px solid #f0e6b8",
                marginBottom: "12px",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "13px", fontWeight: 500, color: "#2c3e50", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {pendingFile.name}
                </div>
                <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
                  {(pendingFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <select
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value)}
                style={{ ...inputStyle, fontSize: "12px", width: "110px" }}
              >
                <option value="attachment">Attachment</option>
                <option value="solicitation">RFP</option>
                <option value="amendment">Amendment</option>
                <option value="pws">SOW / PWS</option>
                <option value="bid_schedule">Pricing</option>
                <option value="other">Other</option>
              </select>
              <button onClick={handleUpload} disabled={uploading} style={btnPrimary}>
                {uploading ? "Uploading…" : "Upload"}
              </button>
              <button onClick={() => setPendingFile(null)} style={btnDanger} disabled={uploading}>
                ✕
              </button>
            </div>
          )}

          {/* Document list */}
          {docsLoading && (
            <p style={{ fontSize: "13px", color: "#888" }}>Fetching documents…</p>
          )}
          {!docsLoading && documents.length === 0 && !pendingFile && (
            <p style={{ fontSize: "13px", color: "#888" }}>
              No documents yet. Click "+ Add Document" to upload files, or accept an opportunity to auto-fetch RFP files.
            </p>
          )}
          {!docsLoading && documents.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {documents.map((doc, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "9px 12px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, marginRight: "10px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#2c3e50",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.file_name}
                    </div>
                    <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
                      {doc.document_type}
                      {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ""}
                    </div>
                  </div>
                  {doc.download_url ? (
                    <a
                      href={doc.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#2980b9",
                        color: "white",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: 600,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Download
                    </a>
                  ) : (
                    <span style={{ fontSize: "12px", color: "#999" }}>Unavailable</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab 2: RFP Analysis ─────────────────────────────────────────────────────

function getDateColor(dateStr: string | null): string {
  if (!dateStr) return "#666";
  const date = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const daysOut = (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  if (daysOut < 0) return "#c0392b";    // red — past
  if (daysOut <= 3) return "#e74c3c";   // red — within 3 days
  if (daysOut <= 7) return "#e67e22";   // orange — within 7 days
  return "#27ae60";                      // green — plenty of time
}

function getDaysLabel(dateStr: string | null): string {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const days = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return `(${Math.abs(days)}d ago)`;
  if (days === 0) return "(today)";
  if (days === 1) return "(tomorrow)";
  return `(${days}d)`;
}

function RfpAnalysisTab({
  analysis,
  analyzing,
  onAnalyze,
  onGenerateSections,
  sectionsExist,
}: {
  analysis: RfpAnalysis | null;
  analyzing: boolean;
  onAnalyze: () => void;
  onGenerateSections: () => void;
  sectionsExist: boolean;
}) {
  const sectionCard: React.CSSProperties = {
    backgroundColor: "white",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "16px",
    marginBottom: "16px",
  };

  const sectionTitle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 700,
    marginBottom: "10px",
    color: "#1a5276",
  };

  // Not started state
  if (!analysis || analysis.status === "not_started") {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
        <h3 style={{ color: "#333", marginBottom: "8px" }}>RFP Analysis</h3>
        <p style={{ color: "#666", marginBottom: "24px", maxWidth: "500px", margin: "0 auto 24px" }}>
          Analyze the attached RFP documents to extract key dates, submission requirements,
          evaluation factors, and forms to complete.
        </p>
        <button
          onClick={onAnalyze}
          disabled={analyzing}
          style={{
            ...btnPrimary,
            padding: "12px 28px",
            fontSize: "15px",
            opacity: analyzing ? 0.6 : 1,
          }}
        >
          {analyzing ? "Analyzing..." : "Analyze RFP Documents"}
        </button>
      </div>
    );
  }

  // Loading / pending state
  if (analysis.status === "pending" || analyzing) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "36px", marginBottom: "16px", animation: "spin 1.5s linear infinite" }}>⏳</div>
        <h3 style={{ color: "#333" }}>Analyzing RFP Documents...</h3>
        <p style={{ color: "#666" }}>
          Extracting text from documents and sending to AI for analysis.
          This may take 15–30 seconds.
        </p>
      </div>
    );
  }

  // Failed state
  if (analysis.status === "failed") {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "36px", marginBottom: "16px" }}>❌</div>
        <h3 style={{ color: "#c0392b" }}>Analysis Failed</h3>
        <p style={{ color: "#666", marginBottom: "16px" }}>{analysis.error || "Unknown error"}</p>
        <button onClick={onAnalyze} style={btnPrimary}>
          Retry Analysis
        </button>
      </div>
    );
  }

  // Complete state — render results
  return (
    <div>
      {/* Header with re-analyze button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, color: "#1a5276" }}>📋 RFP Analysis</h3>
        <button
          onClick={onAnalyze}
          disabled={analyzing}
          style={{ ...btnSecondary, fontSize: "12px", padding: "5px 12px" }}
        >
          {analyzing ? "Analyzing..." : "Re-analyze"}
        </button>
      </div>

      {/* Scope Summary */}
      {analysis.scope_summary && (
        <div style={{ ...sectionCard, backgroundColor: "#f8f9fa", borderLeft: "4px solid #1a5276" }}>
          <div style={{ fontSize: "14px", color: "#333", lineHeight: "1.5" }}>
            {analysis.scope_summary}
          </div>
        </div>
      )}

      {/* Key Dates */}
      {analysis.key_dates && analysis.key_dates.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>⚠️ KEY DATES</div>
          {analysis.key_dates.map((kd, i) => {
            const color = getDateColor(kd.date);
            const daysLabel = getDaysLabel(kd.date);
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  padding: "8px 0",
                  borderBottom: i < analysis.key_dates.length - 1 ? "1px solid #f0f0f0" : "none",
                }}
              >
                <span style={{ color, fontSize: "16px", marginRight: "10px", flexShrink: 0 }}>
                  {color === "#27ae60" ? "🟢" : color === "#e67e22" ? "🟡" : "🔴"}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>
                    {kd.date && (
                      <span style={{ color, marginRight: "8px" }}>
                        {new Date(kd.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                    {kd.event}
                    {kd.mandatory && <span style={{ color: "#c0392b", marginLeft: "6px", fontSize: "11px" }}>MANDATORY</span>}
                    {kd.date && (
                      <span style={{ color: "#999", marginLeft: "8px", fontSize: "11px", fontWeight: 400 }}>
                        {daysLabel}
                      </span>
                    )}
                  </div>
                  {(kd.time || kd.notes) && (
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                      {kd.time && <span>{kd.time}</span>}
                      {kd.time && kd.notes && <span> — </span>}
                      {kd.notes && <span>{kd.notes}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Required Submissions */}
      {analysis.required_submissions && analysis.required_submissions.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>📝 REQUIRED SUBMISSIONS</div>
          {analysis.required_submissions.map((sub, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: i < analysis.required_submissions.length - 1 ? "1px solid #f0f0f0" : "none" }}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <span style={{ marginRight: "8px", fontSize: "14px" }}>☐</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>
                    {sub.item}
                    {sub.mandatory && <span style={{ color: "#c0392b", marginLeft: "6px", fontSize: "11px" }}>Required</span>}
                  </div>
                  {sub.description && (
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{sub.description}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Forms to Complete */}
      {analysis.forms_to_complete && analysis.forms_to_complete.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>📄 FORMS TO COMPLETE</div>
          {analysis.forms_to_complete.map((form, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: i < analysis.forms_to_complete.length - 1 ? "1px solid #f0f0f0" : "none" }}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <span style={{ marginRight: "8px", fontSize: "14px" }}>📑</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>
                    {form.name}
                    {!form.extractable && (
                      <span style={{ backgroundColor: "#f39c12", color: "white", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", marginLeft: "8px" }}>
                        SCANNED
                      </span>
                    )}
                  </div>
                  {form.description && (
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{form.description}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Evaluation Factors */}
      {analysis.evaluation_factors && analysis.evaluation_factors.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>🏆 EVALUATION FACTORS</div>
          {analysis.evaluation_factors.map((ef, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: i < analysis.evaluation_factors.length - 1 ? "1px solid #f0f0f0" : "none" }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>
                {i + 1}. {ef.factor}
                {ef.weight_or_priority && (
                  <span style={{ color: "#1a5276", marginLeft: "8px", fontSize: "12px", fontWeight: 400 }}>
                    ({ef.weight_or_priority})
                  </span>
                )}
              </div>
              {ef.description && (
                <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{ef.description}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Contract Structure */}
      {analysis.contract_structure && (
        <div style={sectionCard}>
          <div style={sectionTitle}>📃 CONTRACT STRUCTURE</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "13px" }}>
            {analysis.contract_structure.contract_type && (
              <div><span style={{ color: "#666" }}>Type:</span> <strong>{analysis.contract_structure.contract_type}</strong></div>
            )}
            {analysis.contract_structure.base_period && (
              <div><span style={{ color: "#666" }}>Base Period:</span> <strong>{analysis.contract_structure.base_period}</strong></div>
            )}
            {analysis.contract_structure.option_periods && (
              <div><span style={{ color: "#666" }}>Options:</span> <strong>{analysis.contract_structure.option_periods}</strong></div>
            )}
            {analysis.contract_structure.total_evaluated_price_method && (
              <div style={{ gridColumn: "1 / -1" }}>
                <span style={{ color: "#666" }}>TEP Method:</span> <strong>{analysis.contract_structure.total_evaluated_price_method}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Response Questions — the core deliverable */}
      {analysis.response_questions && analysis.response_questions.length > 0 && (
        <div style={sectionCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <div style={sectionTitle}>✍️ RESPONSE QUESTIONS ({analysis.response_questions.length})</div>
            <button
              onClick={onGenerateSections}
              style={{
                ...btnPrimary,
                fontSize: "12px",
                padding: "6px 14px",
              }}
            >
              {sectionsExist ? "↻ Rebuild Response Sections" : "→ Generate Response Sections"}
            </button>
          </div>
          <p style={{ fontSize: "12px", color: "#888", margin: "0 0 12px" }}>
            These are the specific questions and topics the RFP requires you to address. Click the button above to create matching sections in the RFP Response tab.
          </p>
          {[...analysis.response_questions]
            .sort((a, b) => a.order - b.order)
            .map((q, i) => (
            <div
              key={q.question_id}
              style={{
                padding: "10px 12px",
                marginBottom: "8px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e8e8e8",
                borderRadius: "6px",
                borderLeft: q.evaluation_link ? "4px solid #1a5276" : "4px solid #bdc3c7",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "13px", color: "#2c3e50", marginBottom: "4px" }}>
                {i + 1}. {q.heading}
              </div>
              <div style={{ fontSize: "13px", color: "#444", lineHeight: "1.5", marginBottom: "4px" }}>
                {q.question}
              </div>
              {q.guidance && (
                <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic", marginBottom: "4px" }}>
                  📌 {q.guidance}
                </div>
              )}
              <div style={{ display: "flex", gap: "12px", fontSize: "11px", color: "#999" }}>
                {q.evaluation_link && (
                  <span>🏆 Eval: <strong style={{ color: "#1a5276" }}>{q.evaluation_link}</strong></span>
                )}
                <span>📄 {q.source_doc}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Other Requirements */}
      {analysis.other_requirements && analysis.other_requirements.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>ℹ️ OTHER REQUIREMENTS</div>
          {analysis.other_requirements.map((req, i) => (
            <div key={i} style={{ padding: "4px 0", fontSize: "13px" }}>
              <span style={{ marginRight: "6px" }}>•</span>
              {req.type && (
                <span style={{
                  backgroundColor: "#eee",
                  padding: "1px 6px",
                  borderRadius: "3px",
                  fontSize: "10px",
                  marginRight: "6px",
                  fontWeight: 600,
                }}>
                  {req.type}
                </span>
              )}
              {req.description}
            </div>
          ))}
        </div>
      )}

      {/* Footer — metadata */}
      <div style={{ fontSize: "11px", color: "#999", textAlign: "center", padding: "8px 0", borderTop: "1px solid #eee" }}>
        Analyzed: {analysis.completed_at ? new Date(analysis.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
        {" | "}Documents: {analysis.documents_analyzed?.length || 0} read, {analysis.documents_scanned?.length || 0} scanned, {analysis.documents_skipped?.length || 0} skipped
        {analysis.model_used && <span> | Model: {analysis.model_used}</span>}
      </div>
    </div>
  );
}

// ── Tab 3: RFP Response ──────────────────────────────────────────────────────

function RFPResponseTab({
  sections,
  templates,
  title,
  status,
  saving,
  lastSaved,
  onSectionChange,
  onSectionHeadingChange,
  addSection,
  removeSection,
  moveSection,
  applyTemplate,
  applyAllTemplates,
  onTitleChange,
  onStatusChange,
  onSave,
  onExport,
}: {
  sections: ResponseSection[];
  templates: ResponseTemplate[];
  title: string;
  status: ProposalResponse["status"];
  saving: boolean;
  lastSaved: string | null;
  onSectionChange: (i: number, content: string) => void;
  onSectionHeadingChange: (i: number, heading: string) => void;
  addSection: () => void;
  removeSection: (i: number) => void;
  moveSection: (i: number, dir: -1 | 1) => void;
  applyTemplate: (t: ResponseTemplate) => void;
  applyAllTemplates: () => void;
  onTitleChange: (v: string) => void;
  onStatusChange: (v: ProposalResponse["status"]) => void;
  onSave: () => void;
  onExport: () => void;
}) {
  const iconBtn: React.CSSProperties = {
    background: "none",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "2px 8px",
    cursor: "pointer",
    fontSize: "12px",
    color: "#666",
  };

  return (
    <div style={{ display: "flex", gap: "20px" }}>
      {/* Sidebar — templates */}
      <div style={{ width: "220px", flexShrink: 0 }}>
        <div style={card}>
          <h3 style={{ margin: "0 0 12px", fontSize: "14px", color: "#2c3e50" }}>Templates</h3>
          {templates.length > 0 ? (
            <>
              <button onClick={applyAllTemplates} style={{ ...btnPrimary, width: "100%", marginBottom: "10px", fontSize: "12px" }}>
                Apply All Templates
              </button>
              {[...templates]
                .sort((a, b) => a.order - b.order)
                .map((t) => (
                  <button
                    key={t._id}
                    onClick={() => applyTemplate(t)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 10px",
                      marginBottom: "6px",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ fontWeight: 500, color: "#2c3e50" }}>{t.title}</div>
                    <div style={{ color: "#999", fontSize: "11px", marginTop: "2px" }}>
                      {t.description?.substring(0, 45) || t.section_type}
                    </div>
                  </button>
                ))}
            </>
          ) : (
            <p style={{ fontSize: "12px", color: "#999" }}>
              No templates configured. Add them in the Admin Panel.
            </p>
          )}
          <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "12px 0" }} />
          <button onClick={addSection} style={{ ...btnSecondary, width: "100%", fontSize: "12px" }}>
            + Add Blank Section
          </button>
        </div>
      </div>

      {/* Main editor */}
      <div style={{ flex: 1 }}>
        {/* Title / status / actions bar */}
        <div
          style={{
            ...card,
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: "14px",
          }}
        >
          <input
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            style={{ ...inputStyle, flex: 1, fontSize: "15px", fontWeight: 600, minWidth: "180px" }}
            placeholder="Response title..."
          />
          <select
            value={status}
            onChange={(e) => onStatusChange(e.target.value as ProposalResponse["status"])}
            style={{ ...inputStyle }}
          >
            <option value="draft">Draft</option>
            <option value="review">In Review</option>
            <option value="final">Final</option>
            <option value="submitted">Submitted</option>
          </select>
          <button onClick={onSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={onExport} style={btnSecondary}>
            Export .txt
          </button>
          {lastSaved && (
            <span style={{ fontSize: "12px", color: "#999" }}>Saved {lastSaved}</span>
          )}
        </div>

        {sections.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "48px",
              color: "#999",
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
            }}
          >
            <p style={{ fontSize: "15px" }}>No sections yet</p>
            <p style={{ fontSize: "13px" }}>Use a template from the sidebar or add a blank section.</p>
          </div>
        )}

        {sections.map((section, index) => (
          <div key={index} style={{ ...card, marginBottom: "10px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <input
                value={section.heading}
                onChange={(e) => onSectionHeadingChange(index, e.target.value)}
                style={{ ...inputStyle, flex: 1, fontSize: "14px", fontWeight: 600, marginRight: "8px" }}
              />
              <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                <button onClick={() => moveSection(index, -1)} disabled={index === 0} style={iconBtn} title="Move up">▲</button>
                <button onClick={() => moveSection(index, 1)} disabled={index === sections.length - 1} style={iconBtn} title="Move down">▼</button>
                <button onClick={() => removeSection(index)} style={{ ...iconBtn, color: "#e74c3c" }} title="Remove">✕</button>
              </div>
            </div>
            <div style={{ fontSize: "11px", color: "#bbb", marginBottom: "6px" }}>{section.section_type}</div>
            <textarea
              value={section.content}
              onChange={(e) => onSectionChange(index, e.target.value)}
              rows={10}
              style={{
                ...inputStyle,
                width: "100%",
                lineHeight: 1.6,
                resize: "vertical",
              }}
              placeholder="Enter section content..."
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab 3: Labor Position Schedule ───────────────────────────────────────────

function LaborTab({
  positions,
  onChange,
  assumptions,
}: {
  positions: LaborPosition[];
  onChange: (positions: LaborPosition[]) => void;
  assumptions: Assumptions;
}) {
  const add = () =>
    onChange([
      ...positions,
      {
        id: newId(),
        position_description: "",
        headcount: 1,
        weekly_hours: 40,
        wage: 0,
        classification: "direct",
      },
    ]);

  const update = (id: string, field: keyof LaborPosition, value: string | number) =>
    onChange(positions.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const remove = (id: string) => onChange(positions.filter((p) => p.id !== id));

  const weeklyTotal = positions.reduce(
    (s, p) => s + (p.headcount || 0) * (p.weekly_hours || 0) * (p.wage || 0),
    0
  );

  const taxIns = calcTaxInsurance(positions, assumptions);

  return (
    <>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#2c3e50" }}>
              Labor Position Schedule
            </h3>
            <p style={{ margin: 0, fontSize: "12px", color: "#888" }}>
              Weekly labor cost: <strong>{fmt(weeklyTotal)}/wk</strong> &nbsp;·&nbsp; Annual:{" "}
              <strong>{fmt(weeklyTotal * 52)}/yr</strong>
            </p>
          </div>
          <button onClick={add} style={btnPrimary}>
            + Add Position
          </button>
        </div>

        {positions.length === 0 ? (
          <p style={{ fontSize: "13px", color: "#999", textAlign: "center", padding: "24px 0" }}>
            No positions added yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={{ ...th, minWidth: "200px" }}>Position Description</th>
                  <th style={{ ...th, width: "80px" }}>Headcount</th>
                  <th style={{ ...th, width: "90px" }}>Hrs/Week</th>
                  <th style={{ ...th, width: "110px" }}>Hourly Wage</th>
                  <th style={{ ...th, width: "100px" }}>Classification</th>
                  <th style={{ ...th, width: "110px" }}>Weekly Cost</th>
                  <th style={{ ...th, width: "60px" }}></th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const weeklyCost = (p.headcount || 0) * (p.weekly_hours || 0) * (p.wage || 0);
                  return (
                    <tr key={p.id}>
                      <td style={td}>
                        <input
                          value={p.position_description}
                          onChange={(e) => update(p.id, "position_description", e.target.value)}
                          style={{ ...inputStyle, width: "100%" }}
                          placeholder="e.g. Program Manager"
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          min={1}
                          value={p.headcount}
                          onChange={(e) => update(p.id, "headcount", Number(e.target.value))}
                          style={{ ...inputStyle, width: "64px" }}
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          min={0}
                          max={168}
                          value={p.weekly_hours}
                          onChange={(e) => update(p.id, "weekly_hours", Number(e.target.value))}
                          style={{ ...inputStyle, width: "72px" }}
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={p.wage}
                          onChange={(e) => update(p.id, "wage", Number(e.target.value))}
                          style={{ ...inputStyle, width: "90px" }}
                          placeholder="$0.00"
                        />
                      </td>
                      <td style={td}>
                        <select
                          value={p.classification}
                          onChange={(e) =>
                            update(p.id, "classification", e.target.value as "direct" | "indirect")
                          }
                          style={{ ...inputStyle, width: "90px" }}
                        >
                          <option value="direct">Direct</option>
                          <option value="indirect">Indirect</option>
                        </select>
                      </td>
                      <td style={{ ...td, fontWeight: 600, color: "#2c3e50" }}>
                        {fmt(weeklyCost)}
                      </td>
                      <td style={td}>
                        <button onClick={() => remove(p.id)} style={btnDanger}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={5}
                    style={{ padding: "10px 6px", fontSize: "13px", fontWeight: 700, color: "#2c3e50" }}
                  >
                    Totals
                  </td>
                  <td style={{ padding: "10px 6px", fontWeight: 700, color: "#2c3e50" }}>
                    {fmt(weeklyTotal)}/wk
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Tax & Insurance — calculated from Assumptions tab */}
      <div style={card}>
        <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#2c3e50" }}>
          Payroll Tax & Insurance
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: "12px", color: "#888" }}>
          Auto-calculated from the labor schedule above and rates on the Assumptions tab. Annual figures.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr>
              <th style={{ ...th, minWidth: "260px" }}>Item</th>
              <th style={{ ...th, width: "280px" }}>Calculation</th>
              <th style={{ ...th, width: "130px", textAlign: "right" }}>Annual Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}><strong>FICA (Employer)</strong></td>
              <td style={{ ...td, color: "#888", fontSize: "12px" }}>
                {fmt(taxIns.totalPayroll)} payroll × 6.2%
              </td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(taxIns.fica)}</td>
            </tr>
            <tr>
              <td style={td}><strong>Medicare (Employer)</strong></td>
              <td style={{ ...td, color: "#888", fontSize: "12px" }}>
                {fmt(taxIns.totalPayroll)} payroll × 1.45%
              </td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(taxIns.medicare)}</td>
            </tr>
            <tr>
              <td style={td}><strong>Unemployment</strong></td>
              <td style={{ ...td, color: "#888", fontSize: "12px" }}>
                {fmt(assumptions.unemployment_wage_base)} base × {assumptions.unemployment_pct}% × {taxIns.totalHeadcount} headcount × {assumptions.unemployment_headcount_pct}%
              </td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(taxIns.unemployment)}</td>
            </tr>
            <tr>
              <td style={td}><strong>Workers' Comp</strong></td>
              <td style={{ ...td, color: "#888", fontSize: "12px" }}>
                {fmt(taxIns.totalPayroll)} payroll × {assumptions.workers_comp_rate}%
              </td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(taxIns.workersComp)}</td>
            </tr>
            <tr>
              <td style={td}><strong>General Liability</strong></td>
              <td style={{ ...td, color: "#888", fontSize: "12px" }}>
                {fmt(taxIns.totalPayroll)} payroll × {assumptions.general_liability_pct}%
              </td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(taxIns.generalLiability)}</td>
            </tr>
            {(assumptions.other_insurance || []).map((o) => (
              <tr key={o.id}>
                <td style={td}><strong>{o.description || "Other Insurance"}</strong></td>
                <td style={{ ...td, color: "#888", fontSize: "12px" }}>Fixed annual</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(o.annual_cost || 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#f8f9fa" }}>
              <td colSpan={2} style={{ padding: "10px 6px", fontSize: "14px", fontWeight: 700, color: "#2c3e50" }}>
                Total Tax & Insurance
              </td>
              <td style={{ padding: "10px 6px", fontSize: "15px", fontWeight: 700, color: "#1a5276", textAlign: "right" }}>
                {fmt(taxIns.taxInsTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Employee Benefits — separate section below Tax & Insurance */}
      <div style={card}>
        <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#2c3e50" }}>
          Employee Benefits
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: "12px", color: "#888" }}>
          Calculated from total headcount and benefit rates on the Assumptions tab. Annual figures.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr>
              <th style={{ ...th, minWidth: "260px" }}>Item</th>
              <th style={{ ...th, width: "280px" }}>Calculation</th>
              <th style={{ ...th, width: "130px", textAlign: "right" }}>Annual Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}><strong>Employee Benefits</strong></td>
              <td style={{ ...td, color: "#888", fontSize: "12px" }}>
                {taxIns.totalHeadcount} headcount × {fmt(assumptions.employee_benefits_cost)}/yr × {assumptions.benefits_participation_pct}%
              </td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(taxIns.benefits)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#f8f9fa" }}>
              <td colSpan={2} style={{ padding: "10px 6px", fontSize: "14px", fontWeight: 700, color: "#2c3e50" }}>
                Total Employee Benefits
              </td>
              <td style={{ padding: "10px 6px", fontSize: "15px", fontWeight: 700, color: "#9b59b6", textAlign: "right" }}>
                {fmt(taxIns.benefits)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

// ── Tab 5: Material Schedule + Site Overhead ─────────────────────────────────

function MaterialsTab({
  materials,
  overhead,
  onMaterialsChange,
  onOverheadChange,
}: {
  materials: MaterialItem[];
  overhead: OverheadItem[];
  onMaterialsChange: (m: MaterialItem[]) => void;
  onOverheadChange: (o: OverheadItem[]) => void;
}) {
  const add = () =>
    onMaterialsChange([
      ...materials,
      { id: newId(), description: "", quantity: 1, unit_cost: 0 },
    ]);

  const update = (id: string, field: keyof MaterialItem, value: string | number) =>
    onMaterialsChange(materials.map((m) => (m.id === id ? { ...m, [field]: value } : m)));

  const remove = (id: string) => onMaterialsChange(materials.filter((m) => m.id !== id));

  const totalMaterials = materials.reduce((s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0);

  // Overhead helpers
  const addOverhead = () =>
    onOverheadChange([...overhead, { id: newId(), description: "", amount: 0 }]);

  const updateOverhead = (id: string, field: keyof OverheadItem, value: string | number) =>
    onOverheadChange(overhead.map((o) => (o.id === id ? { ...o, [field]: value } : o)));

  const removeOverhead = (id: string) => onOverheadChange(overhead.filter((o) => o.id !== id));

  const totalOverhead = overhead.reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <>
      {/* Material Schedule */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#2c3e50" }}>
              Material Schedule
            </h3>
            <p style={{ margin: 0, fontSize: "12px", color: "#888" }}>
              Total materials: <strong>{fmt(totalMaterials)}</strong>
            </p>
          </div>
          <button onClick={add} style={btnPrimary}>
            + Add Item
          </button>
        </div>

        {materials.length === 0 ? (
          <p style={{ fontSize: "13px", color: "#999", textAlign: "center", padding: "24px 0" }}>
            No materials added yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={{ ...th, minWidth: "260px" }}>Description</th>
                  <th style={{ ...th, width: "90px" }}>Quantity</th>
                  <th style={{ ...th, width: "120px" }}>Unit Cost</th>
                  <th style={{ ...th, width: "120px" }}>Total Cost</th>
                  <th style={{ ...th, width: "60px" }}></th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m) => {
                  const total = (m.quantity || 0) * (m.unit_cost || 0);
                  return (
                    <tr key={m.id}>
                      <td style={td}>
                        <input
                          value={m.description}
                          onChange={(e) => update(m.id, "description", e.target.value)}
                          style={{ ...inputStyle, width: "100%" }}
                          placeholder="e.g. Laptop computers"
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          min={0}
                          value={m.quantity}
                          onChange={(e) => update(m.id, "quantity", Number(e.target.value))}
                          style={{ ...inputStyle, width: "72px" }}
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={m.unit_cost}
                          onChange={(e) => update(m.id, "unit_cost", Number(e.target.value))}
                          style={{ ...inputStyle, width: "100px" }}
                          placeholder="$0.00"
                        />
                      </td>
                      <td style={{ ...td, fontWeight: 600, color: "#2c3e50" }}>{fmt(total)}</td>
                      <td style={td}>
                        <button onClick={() => remove(m.id)} style={btnDanger}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={3}
                    style={{ padding: "10px 6px", fontSize: "13px", fontWeight: 700, color: "#2c3e50" }}
                  >
                    Total Materials
                  </td>
                  <td style={{ padding: "10px 6px", fontWeight: 700, color: "#2c3e50" }}>
                    {fmt(totalMaterials)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Site Overhead */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#2c3e50" }}>
              Site Overhead
            </h3>
            <p style={{ margin: 0, fontSize: "12px", color: "#888" }}>
              Total overhead: <strong>{fmt(totalOverhead)}</strong>
            </p>
          </div>
          <button onClick={addOverhead} style={btnPrimary}>
            + Add Item
          </button>
        </div>

        {overhead.length === 0 ? (
          <p style={{ fontSize: "13px", color: "#999", textAlign: "center", padding: "24px 0" }}>
            No overhead items added yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={{ ...th, minWidth: "300px" }}>Description</th>
                  <th style={{ ...th, width: "150px" }}>Amount</th>
                  <th style={{ ...th, width: "60px" }}></th>
                </tr>
              </thead>
              <tbody>
                {overhead.map((o) => (
                  <tr key={o.id}>
                    <td style={td}>
                      <input
                        value={o.description}
                        onChange={(e) => updateOverhead(o.id, "description", e.target.value)}
                        style={{ ...inputStyle, width: "100%" }}
                        placeholder="e.g. Office space, utilities"
                      />
                    </td>
                    <td style={td}>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={o.amount}
                        onChange={(e) => updateOverhead(o.id, "amount", Number(e.target.value))}
                        style={{ ...inputStyle, width: "120px" }}
                        placeholder="$0.00"
                      />
                    </td>
                    <td style={td}>
                      <button onClick={() => removeOverhead(o.id)} style={btnDanger}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: "10px 6px", fontSize: "13px", fontWeight: 700, color: "#2c3e50" }}>
                    Total Overhead
                  </td>
                  <td style={{ padding: "10px 6px", fontWeight: 700, color: "#2c3e50" }}>
                    {fmt(totalOverhead)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Tab 5: Revenue Schedule ───────────────────────────────────────────────────

function RevenueTab({
  items,
  onChange,
}: {
  items: RevenueItem[];
  onChange: (items: RevenueItem[]) => void;
}) {
  const add = () =>
    onChange([...items, { id: newId(), period: "", revenue: 0 }]);

  const update = (id: string, field: keyof RevenueItem, value: string | number) =>
    onChange(items.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const remove = (id: string) => onChange(items.filter((r) => r.id !== id));

  const total = items.reduce((s, r) => s + (r.revenue || 0), 0);

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div>
          <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#2c3e50" }}>
            Revenue Schedule
          </h3>
          <p style={{ margin: 0, fontSize: "12px", color: "#888" }}>
            Total contract revenue: <strong style={{ color: "#27ae60" }}>{fmt(total)}</strong>
          </p>
        </div>
        <button onClick={add} style={btnPrimary}>
          + Add Period
        </button>
      </div>

      {items.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#999", textAlign: "center", padding: "24px 0" }}>
          No revenue periods added yet.
        </p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr>
              <th style={{ ...th, minWidth: "220px" }}>Period</th>
              <th style={{ ...th, width: "160px" }}>Revenue ($)</th>
              <th style={{ ...th, width: "60px" }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td style={td}>
                  <input
                    value={r.period}
                    onChange={(e) => update(r.id, "period", e.target.value)}
                    style={{ ...inputStyle, width: "100%" }}
                    placeholder="e.g. Year 1, Option Year 1, Month 1"
                  />
                </td>
                <td style={td}>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={r.revenue}
                    onChange={(e) => update(r.id, "revenue", Number(e.target.value))}
                    style={{ ...inputStyle, width: "140px" }}
                    placeholder="$0.00"
                  />
                </td>
                <td style={td}>
                  <button onClick={() => remove(r.id)} style={btnDanger}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ padding: "10px 6px", fontSize: "13px", fontWeight: 700, color: "#2c3e50" }}>
                Total Revenue
              </td>
              <td style={{ padding: "10px 6px", fontWeight: 700, color: "#27ae60", fontSize: "15px" }}>
                {fmt(total)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

// ── Tab 7: Assumptions ───────────────────────────────────────────────────────

function AssumptionsTab({
  assumptions,
  onChange,
}: {
  assumptions: Assumptions;
  onChange: (a: Assumptions) => void;
}) {
  const set = (field: keyof Assumptions, value: number) =>
    onChange({ ...assumptions, [field]: value });

  const addOther = () =>
    onChange({
      ...assumptions,
      other_insurance: [
        ...(assumptions.other_insurance || []),
        { id: newId(), description: "", annual_cost: 0 },
      ],
    });

  const updateOther = (id: string, field: keyof OtherInsuranceItem, value: string | number) =>
    onChange({
      ...assumptions,
      other_insurance: (assumptions.other_insurance || []).map((o) =>
        o.id === id ? { ...o, [field]: value } : o
      ),
    });

  const removeOther = (id: string) =>
    onChange({
      ...assumptions,
      other_insurance: (assumptions.other_insurance || []).filter((o) => o.id !== id),
    });

  // ── Compact styles ──
  const sectionCard: React.CSSProperties = {
    backgroundColor: "white", borderRadius: "8px", border: "1px solid #e0e0e0",
    padding: "14px 18px", marginBottom: "12px",
  };
  const sectionTitle: React.CSSProperties = {
    margin: "0 0 10px", fontSize: "13px", fontWeight: 700, color: "#2c3e50",
    textTransform: "uppercase" as const, letterSpacing: "0.3px",
  };
  const grid2: React.CSSProperties = {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px",
  };
  const grid4: React.CSSProperties = {
    display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px 16px",
  };
  const fieldRow: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "10px", minHeight: "32px",
  };
  const lbl: React.CSSProperties = {
    fontSize: "12px", fontWeight: 600, color: "#666", whiteSpace: "nowrap" as const,
  };
  const numInput: React.CSSProperties = {
    ...inputStyle, width: "110px", textAlign: "right" as const,
  };
  const narrowInput: React.CSSProperties = {
    ...inputStyle, width: "80px", textAlign: "right" as const,
  };
  const adornment: React.CSSProperties = {
    fontSize: "12px", color: "#999", fontWeight: 600, minWidth: "14px",
  };

  return (
    <>
      {/* Row 1: Contract + Insurance & Taxes side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: "12px", marginBottom: "0" }}>

        {/* Contract */}
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Contract</h3>
          <div style={fieldRow}>
            <label style={lbl}>Contract Years</label>
            <select
              value={assumptions.contract_years}
              onChange={(e) => set("contract_years", Number(e.target.value))}
              style={{ ...inputStyle, width: "70px", textAlign: "center" as const }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Insurance & Taxes */}
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Insurance & Taxes</h3>
          <div style={grid2}>
            <div style={fieldRow}>
              <label style={lbl}>Workers' Comp Rate</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} step={0.01} value={assumptions.workers_comp_rate} onChange={(e) => set("workers_comp_rate", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>General Liability</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} step={0.01} value={assumptions.general_liability_pct} onChange={(e) => set("general_liability_pct", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>Pass-Through Rate</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} step={0.01} value={assumptions.passthrough_rate_pct} onChange={(e) => set("passthrough_rate_pct", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>City Tax Rate</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} step={0.01} value={assumptions.city_tax_rate_pct} onChange={(e) => set("city_tax_rate_pct", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Unemployment + Employee Benefits side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "0" }}>

        {/* Unemployment */}
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Unemployment</h3>
          <div style={{ display: "flex", flexDirection: "column" as const, gap: "8px" }}>
            <div style={fieldRow}>
              <label style={lbl}>Rate</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} step={0.01} value={assumptions.unemployment_pct} onChange={(e) => set("unemployment_pct", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>Wage Base (per employee)</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={adornment}>$</span>
                <input type="number" min={0} step={1} value={assumptions.unemployment_wage_base} onChange={(e) => set("unemployment_wage_base", Number(e.target.value))} style={numInput} />
              </span>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>Subject Headcount</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} max={100} step={0.1} value={assumptions.unemployment_headcount_pct} onChange={(e) => set("unemployment_headcount_pct", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
          </div>
        </div>

        {/* Employee Benefits */}
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Employee Benefits</h3>
          <div style={{ display: "flex", flexDirection: "column" as const, gap: "8px" }}>
            <div style={fieldRow}>
              <label style={lbl}>Cost per Employee / Year</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={adornment}>$</span>
                <input type="number" min={0} step={1} value={assumptions.employee_benefits_cost} onChange={(e) => set("employee_benefits_cost", Number(e.target.value))} style={numInput} />
              </span>
            </div>
            <div style={fieldRow}>
              <label style={lbl}>Participation Rate</label>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input type="number" min={0} max={100} step={0.1} value={assumptions.benefits_participation_pct} onChange={(e) => set("benefits_participation_pct", Number(e.target.value))} style={narrowInput} />
                <span style={adornment}>%</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Annual Escalation — 4-column single card */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Annual Escalation</h3>
        <div style={grid4}>
          <div style={fieldRow}>
            <label style={lbl}>Revenue</label>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input type="number" min={0} step={0.1} value={assumptions.yearly_revenue_increase_pct} onChange={(e) => set("yearly_revenue_increase_pct", Number(e.target.value))} style={narrowInput} />
              <span style={adornment}>%</span>
            </span>
          </div>
          <div style={fieldRow}>
            <label style={lbl}>Wages</label>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input type="number" min={0} step={0.1} value={assumptions.yearly_wage_increase_pct} onChange={(e) => set("yearly_wage_increase_pct", Number(e.target.value))} style={narrowInput} />
              <span style={adornment}>%</span>
            </span>
          </div>
          <div style={fieldRow}>
            <label style={lbl}>Materials</label>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input type="number" min={0} step={0.1} value={assumptions.yearly_materials_increase_pct} onChange={(e) => set("yearly_materials_increase_pct", Number(e.target.value))} style={narrowInput} />
              <span style={adornment}>%</span>
            </span>
          </div>
          <div style={fieldRow}>
            <label style={lbl}>Overhead</label>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input type="number" min={0} step={0.1} value={assumptions.yearly_overhead_increase_pct} onChange={(e) => set("yearly_overhead_increase_pct", Number(e.target.value))} style={narrowInput} />
              <span style={adornment}>%</span>
            </span>
          </div>
        </div>
      </div>

      {/* Row 4: Other Insurance */}
      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h3 style={{ ...sectionTitle, margin: 0 }}>Other Insurance</h3>
          <button onClick={addOther} style={{ ...btnPrimary, padding: "4px 12px", fontSize: "12px" }}>+ Add Line</button>
        </div>
        {(assumptions.other_insurance || []).length === 0 ? (
          <p style={{ fontSize: "12px", color: "#999", textAlign: "center", padding: "8px 0", margin: 0 }}>No other insurance items.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={{ ...th, minWidth: "260px" }}>Description</th>
                <th style={{ ...th, width: "140px" }}>Annual Cost</th>
                <th style={{ ...th, width: "50px" }}></th>
              </tr>
            </thead>
            <tbody>
              {(assumptions.other_insurance || []).map((o) => (
                <tr key={o.id}>
                  <td style={td}>
                    <input value={o.description} onChange={(e) => updateOther(o.id, "description", e.target.value)} style={{ ...inputStyle, width: "100%" }} placeholder="e.g. Umbrella Policy" />
                  </td>
                  <td style={td}>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span style={adornment}>$</span>
                      <input type="number" min={0} step={1} value={o.annual_cost} onChange={(e) => updateOther(o.id, "annual_cost", Number(e.target.value))} style={{ ...inputStyle, width: "110px", textAlign: "right" as const }} />
                    </span>
                  </td>
                  <td style={td}>
                    <button onClick={() => removeOther(o.id)} style={btnDanger}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ── Main WorkspacePage ────────────────────────────────────────────────────────

const TABS = [
  "Summary & Files",
  "RFP Analysis",
  "RFP Response",
  "Assumptions",
  "Labor Schedule",
  "Materials & Overhead",
  "Revenue Schedule",
];

function WorkspacePage() {
  const { oppId } = useParams<{ oppId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [documents, setDocuments] = useState<OpportunityDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // ── Response / schedule state ───────────────────────────────────────────────
  const [sections, setSections] = useState<ResponseSection[]>([]);
  const [title, setTitle] = useState("Draft Response");
  const [respStatus, setRespStatus] = useState<ProposalResponse["status"]>("draft");
  const [laborSchedule, setLaborSchedule] = useState<LaborPosition[]>([]);
  const [materialSchedule, setMaterialSchedule] = useState<MaterialItem[]>([]);
  const [siteOverhead, setSiteOverhead] = useState<OverheadItem[]>([]);
  const [revenueSchedule, setRevenueSchedule] = useState<RevenueItem[]>([]);
  const [assumptions, setAssumptions] = useState<Assumptions>({ ...DEFAULT_ASSUMPTIONS });

  // ── RFP Analysis state ──────────────────────────────────────────────────────
  const [rfpAnalysis, setRfpAnalysis] = useState<RfpAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // ── Save state ──────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const responseIdRef = useRef<string | null>(null);
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const doSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!oppId) return;
    const load = async () => {
      try {
        setLoading(true);
        const [opp, resData, tmpl] = await Promise.all([
          getOpportunity(oppId),
          getResponses(oppId),
          getTemplates(),
        ]);
        setOpportunity(opp);
        setTemplates(tmpl);

        if (resData.responses.length > 0) {
          const existing = resData.responses[0];
          responseIdRef.current = existing._id;
          setSections(existing.sections || []);
          setTitle(existing.title);
          setRespStatus(existing.status);
          setLaborSchedule(existing.labor_schedule || []);
          setMaterialSchedule(existing.material_schedule || []);
          // Migrate legacy single-object overhead to array format
          const rawOh = existing.site_overhead;
          if (Array.isArray(rawOh)) {
            setSiteOverhead(rawOh);
          } else if (rawOh && typeof rawOh === "object" && (rawOh as any).description) {
            setSiteOverhead([{ id: newId(), description: (rawOh as any).description, amount: (rawOh as any).amount || 0 }]);
          } else {
            setSiteOverhead([]);
          }
          setRevenueSchedule(existing.revenue_schedule || []);
          setAssumptions(existing.assumptions || { ...DEFAULT_ASSUMPTIONS });
        }

        // Load documents async — don't block render
        setDocsLoading(true);
        getDocuments(oppId)
          .then((r) => setDocuments(r.documents))
          .catch(() => {})
          .finally(() => setDocsLoading(false));

        // Load existing RFP analysis (if any)
        getRfpAnalysis(oppId)
          .then((r) => {
            if (r.analysis) setRfpAnalysis(r.analysis);
          })
          .catch(() => {});
      } catch (err: any) {
        setError(err?.response?.data?.error || "Failed to load workspace");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [oppId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh documents list (called after upload)
  const refreshDocuments = useCallback(() => {
    if (!oppId) return;
    setDocsLoading(true);
    getDocuments(oppId)
      .then((r) => setDocuments(r.documents))
      .catch(() => {})
      .finally(() => setDocsLoading(false));
  }, [oppId]);

  // ── Save ────────────────────────────────────────────────────────────────────
  const doSave = useCallback(async () => {
    if (!oppId) return;
    try {
      setSaving(true);
      const payload = {
        opportunity_id: oppId,
        title,
        sections,
        status: respStatus,
        labor_schedule: laborSchedule,
        material_schedule: materialSchedule,
        site_overhead: siteOverhead,
        revenue_schedule: revenueSchedule,
        assumptions,
      };
      if (responseIdRef.current) {
        await updateResponse(responseIdRef.current, payload);
      } else {
        const result = await saveResponse(payload);
        responseIdRef.current = result.response_id;
      }
      setLastSaved(new Date().toLocaleTimeString());
    } catch {
      // Silent — user can retry
    } finally {
      setSaving(false);
    }
  }, [oppId, title, sections, respStatus, laborSchedule, materialSchedule, siteOverhead, revenueSchedule, assumptions]);

  // Auto-save 30s after any content change
  useEffect(() => {
    if (loading) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(doSave, 30000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [sections, laborSchedule, materialSchedule, siteOverhead, revenueSchedule, assumptions, doSave, loading]);

  // Keep ref current for unmount save
  useEffect(() => { doSaveRef.current = doSave; }, [doSave]);

  // Save on unmount (navigation away)
  useEffect(() => {
    return () => { doSaveRef.current?.(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── RFP Analysis handler ───────────────────────────────────────────────────
  const handleAnalyzeRfp = useCallback(async () => {
    if (!oppId) return;
    try {
      setAnalyzing(true);
      setRfpAnalysis((prev) => prev ? { ...prev, status: "pending" } : { status: "pending" } as any);
      const result = await analyzeRfp(oppId);
      setRfpAnalysis(result.analysis);
    } catch (err: any) {
      setRfpAnalysis({
        status: "failed",
        error: err?.response?.data?.error || "Analysis failed",
      } as any);
    } finally {
      setAnalyzing(false);
    }
  }, [oppId]);

  // ── Generate response sections from RFP analysis questions ─────────────────
  const handleGenerateSections = useCallback(() => {
    if (!rfpAnalysis?.response_questions?.length) return;
    const questions = [...rfpAnalysis.response_questions].sort((a, b) => a.order - b.order);
    const newSections: ResponseSection[] = questions.map((q, i) => ({
      section_type: q.question_id,
      heading: q.heading,
      content: `[RFP QUESTION]\n${q.question}${q.guidance ? `\n\n[GUIDANCE]\n${q.guidance}` : ""}${q.evaluation_link ? `\n\n[EVALUATION FACTOR: ${q.evaluation_link}]` : ""}\n\n---\n\n`,
      order: i,
    }));
    setSections(newSections);
    setActiveTab(2); // Switch to RFP Response tab
  }, [rfpAnalysis]);

  // ── Section handlers ────────────────────────────────────────────────────────
  const handleSectionChange = (i: number, content: string) =>
    setSections((prev) => {
      const u = [...prev];
      u[i] = { ...u[i], content };
      return u;
    });

  const handleSectionHeadingChange = (i: number, heading: string) =>
    setSections((prev) => {
      const u = [...prev];
      u[i] = { ...u[i], heading };
      return u;
    });

  const addSection = () =>
    setSections((prev) => [
      ...prev,
      { section_type: "custom", heading: "New Section", content: "", order: prev.length },
    ]);

  const removeSection = (i: number) => setSections((prev) => prev.filter((_, idx) => idx !== i));

  const moveSection = (i: number, dir: -1 | 1) => {
    const ni = i + dir;
    if (ni < 0 || ni >= sections.length) return;
    setSections((prev) => {
      const u = [...prev];
      [u[i], u[ni]] = [u[ni], u[i]];
      return u.map((s, idx) => ({ ...s, order: idx }));
    });
  };

  const applyTemplate = (t: ResponseTemplate) =>
    setSections((prev) => [
      ...prev,
      { section_type: t.section_type, heading: t.title, content: t.content, order: prev.length },
    ]);

  const applyAllTemplates = () => {
    const sorted = [...templates].sort((a, b) => a.order - b.order);
    setSections(
      sorted.map((t, i) => ({
        section_type: t.section_type,
        heading: t.title,
        content: t.content,
        order: i,
      }))
    );
  };

  const exportToText = () => {
    if (!opportunity) return;
    const lines = [
      `PROPOSAL RESPONSE: ${title}`,
      `Opportunity: ${opportunity.title}`,
      `Agency: ${opportunity.agency}`,
      `Date: ${new Date().toLocaleDateString("en-US")}`,
      "",
      "=".repeat(60),
      "",
    ];
    sections.forEach((s) => {
      lines.push(s.heading.toUpperCase());
      lines.push("-".repeat(40));
      lines.push(s.content);
      lines.push("");
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `response-${opportunity.highergov_id || "draft"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Tax/Insurance + Budget ──────────────────────────────────────────────────
  const taxIns = calcTaxInsurance(laborSchedule, assumptions);
  const budget = calcBudget(laborSchedule, materialSchedule, siteOverhead, revenueSchedule, taxIns.taxInsTotal, taxIns.benefits);

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f0f2f5",
          fontFamily: "Segoe UI, sans-serif",
        }}
      >
        <p style={{ color: "#666" }}>Loading workspace...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f0f2f5",
          fontFamily: "Segoe UI, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#c0392b" }}>{error}</p>
          <button
            onClick={() => navigate("/")}
            style={{ padding: "8px 16px", backgroundColor: "#1a5276", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
          >
            Back to Queue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f0f2f5", fontFamily: "Segoe UI, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          backgroundColor: "#1a5276",
          color: "white",
          padding: "12px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={() => navigate("/")}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>
              Opportunity Workspace
            </h1>
            <span style={{ fontSize: "12px", opacity: 0.8 }}>
              {opportunity?.title?.substring(0, 70) || ""}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "12px" }}>
          {lastSaved && <span style={{ opacity: 0.7 }}>Saved {lastSaved}</span>}
          {saving && <span style={{ opacity: 0.7 }}>Saving...</span>}
          <button
            onClick={doSave}
            disabled={saving}
            style={{
              padding: "5px 14px",
              backgroundColor: "#27ae60",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              opacity: saving ? 0.6 : 1,
            }}
          >
            Save All
          </button>
          <span>{user?.name}</span>
          <button
            onClick={logout}
            style={{
              padding: "4px 10px",
              backgroundColor: "rgba(255,255,255,0.15)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "11px",
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
          padding: "0 24px",
          display: "flex",
          gap: "0",
        }}
      >
        {TABS.map((label, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            style={{
              padding: "12px 20px",
              border: "none",
              borderBottom: activeTab === i ? "3px solid #1a5276" : "3px solid transparent",
              backgroundColor: "transparent",
              color: activeTab === i ? "#1a5276" : "#666",
              fontWeight: activeTab === i ? 600 : 400,
              fontSize: "14px",
              cursor: "pointer",
              fontFamily: "Segoe UI, sans-serif",
              transition: "color 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: "1300px", margin: "0 auto", padding: "24px" }}>
        {activeTab === 0 && opportunity && (
          <InfoTab
            opp={opportunity}
            documents={documents}
            docsLoading={docsLoading}
            budget={budget}
            taxIns={taxIns}
            oppId={oppId || ""}
            onDocumentsRefresh={refreshDocuments}
          />
        )}

        {activeTab === 1 && (
          <RfpAnalysisTab
            analysis={rfpAnalysis}
            analyzing={analyzing}
            onAnalyze={handleAnalyzeRfp}
            onGenerateSections={handleGenerateSections}
            sectionsExist={sections.length > 0}
          />
        )}

        {activeTab === 2 && (
          <RFPResponseTab
            sections={sections}
            templates={templates}
            title={title}
            status={respStatus}
            saving={saving}
            lastSaved={lastSaved}
            onSectionChange={handleSectionChange}
            onSectionHeadingChange={handleSectionHeadingChange}
            addSection={addSection}
            removeSection={removeSection}
            moveSection={moveSection}
            applyTemplate={applyTemplate}
            applyAllTemplates={applyAllTemplates}
            onTitleChange={setTitle}
            onStatusChange={setRespStatus}
            onSave={doSave}
            onExport={exportToText}
          />
        )}

        {activeTab === 3 && (
          <AssumptionsTab assumptions={assumptions} onChange={setAssumptions} />
        )}

        {activeTab === 4 && (
          <LaborTab positions={laborSchedule} onChange={setLaborSchedule} assumptions={assumptions} />
        )}

        {activeTab === 5 && (
          <MaterialsTab
            materials={materialSchedule}
            overhead={siteOverhead}
            onMaterialsChange={setMaterialSchedule}
            onOverheadChange={setSiteOverhead}
          />
        )}

        {activeTab === 6 && (
          <RevenueTab items={revenueSchedule} onChange={setRevenueSchedule} />
        )}
      </div>
    </div>
  );
}

export default WorkspacePage;
